import React, { useState, useEffect, useRef } from "react";
import { Box, Grid, IconButton, Paper, MenuList, MenuItem, ClickAwayListener, Popper, Typography } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import "animate.css/animate.css";
import httpRequest from "../../HttpService";
import styles from './inputFields/styles.js';

const sxNameInputContainer = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
};
const sxInputPartBase = {
  display: 'flex',
  height: '40px',
  width: '100%',
  alignItems: 'center',
  backgroundColor: 'white',
  borderRadius: '0rem .25rem .25rem 0rem',
  border: '1px solid #ced4da',
};
const sxInputPartError = {
  border: '2px solid',
  borderColor: 'error.main',
  marginLeft: '-1px',
};
const sxInput = {
  flexGrow: 1,
  border: 'none',
  outline: 'none',
  fontSize: '14px',
  backgroundColor: 'transparent',
  padding: '10px 14px',
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  '&:disabled': {
    cursor: 'not-allowed',
    color: 'rgba(0, 0, 0, 0.38)',
  },
};
const sxDropdownIcon = {
  cursor: 'pointer',
  color: 'rgba(0, 0, 0, 0.54)',
  marginRight: '8px',
  '&:hover': {
    color: 'rgba(0, 0, 0, 0.87)',
  },
};
const sxMenuPaper = { maxHeight: '300px', maxWidth: '400px' };
const sxMenuItem = { fontSize: '14px' };
const sxHelperText = {
  fontSize: '0.75rem',
  color: 'error.main',
  marginTop: '3px',
  marginLeft: '14px',
  marginRight: '14px',
};

const DropdownBox = (props) => {
  const [accountId, setAccountId] = useState(props.value);
  const [callerName, setCallerName] = useState(null);
  const [callerOptions, setCallerOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentQuoteId, setCurrentQuoteId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [skipLocalStorage, setSkipLocalStorage] = useState(false);
  const [augmentedChoices, setAugmentedChoices] = useState(props.choices || []);

  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const isLoadingQuote = useRef(false);
  const hasSetInitialCaller = useRef(false);
  const previousCaller = useRef(null);
  const hasSetAccountId = useRef(false);
  const hasHandledAccountSwitch = useRef(null); // Track which quote we've switched for
  const [isUserTyping, setIsUserTyping] = useState(false);

  // Update accountId when props.value changes (from parent component)
  useEffect(() => {
    console.log(`🔄 DropdownBox props.value sync check:`, {
      propsValue: props.value,
      currentAccountId: accountId,
      willUpdate: props.value && props.value !== accountId,
      augmentedChoicesCount: augmentedChoices.length,
      isInChoices: augmentedChoices.some(choice => choice.value === props.value)
    });

    if (props.value && props.value !== accountId) {
      console.log(`✅ DropdownBox: Updating accountId from ${accountId} to ${props.value}`);
      setAccountId(props.value);
    }
  }, [props.value, augmentedChoices]);

  // Update augmented choices when props.choices changes
  // NOTE: We no longer need to add the quote's account to the list because
  // the parent component (NewOrder) now properly switches account groups
  // by calling getAccountDetail() which loads the correct group's accounts
  useEffect(() => {
    console.log("🎯 Choices Update:", {
      choicesCount: props.choices?.length,
      currentAccount: accountId
    });

    setAugmentedChoices([...(props.choices || [])]);
  }, [props.choices]);

  // Initialize accountId immediately on mount
  // DON'T run this - let loadQuote set the account if loading from URL
  // useEffect(() => {
  //   if (accountId && accountId !== "select") {
  //     console.log("🔧 Setting initial accountId in parent:", accountId);
  //     props.onChange({"target": "accountId", "value": accountId});
  //   }
  // }, []); // Run once on mount

  // Update accountId when quote is loaded with a different account
  // This handles the case where a quote from a different account is loaded
  useEffect(() => {
    console.log("🔍 Account switch check:", {
      quoteAccount: props.info[0]?.accountId,
      currentAccount: accountId,
      loadedQuoteId: props.loadedQuoteId,
      alreadyHandled: hasHandledAccountSwitch.current,
      willSwitch: props.info[0]?.accountId && props.loadedQuoteId && props.info[0].accountId !== accountId && hasHandledAccountSwitch.current !== props.loadedQuoteId
    });

    // Only switch if:
    // 1. Quote has an account
    // 2. Quote is loaded (has loadedQuoteId)
    // 3. Quote account differs from current account
    // 4. We haven't already handled this quote
    if (props.info[0]?.accountId &&
        props.loadedQuoteId &&
        props.info[0].accountId !== accountId &&
        hasHandledAccountSwitch.current !== props.loadedQuoteId) {

      console.log("🔄 SWITCHING ACCOUNT for loaded quote - from", accountId, "to", props.info[0].accountId);
      const newAccount = props.info[0].accountId;

      // Mark this quote as handled to prevent infinite loop
      hasHandledAccountSwitch.current = props.loadedQuoteId;

      // Reset state for the new account
      setCallerName(null); // Will be set by the initializeCaller effect
      setInputValue('');
      setCallerOptions([]); // Clear options, will reload for new account
      setIsInitialized(false); // Reset to re-initialize with new account
      hasSetInitialCaller.current = false;
      hasSetAccountId.current = false;
      previousCaller.current = null;
      isLoadingQuote.current = false;

      // DON'T clear currentQuoteId - we're loading this quote!
      // DON'T call setAccountId here - it will be set by the props.value sync effect
      // DON'T notify parent - parent is the one that initiated this change
    }
  }, [props.info[0]?.accountId, props.loadedQuoteId, accountId]);

  // Initialize from URL quote first, then localStorage (but NOT after account change), then null
  useEffect(() => {
    if (!accountId) return;

    const initializeCaller = async () => {

      // Priority 1: Loading from URL quote - check if we have a loadedQuoteId
      // Use loadedQuoteId to detect if this is a quote loaded from URL (instead of isLoadingQuote which gets set to false too early)
      // This check runs EVEN IF already initialized, because quote loading should override everything
      // NOTE: We don't require caller to be present - old quotes may have null callers!
      if (props.info[0]?.quoteId && props.loadedQuoteId === props.info[0]?.quoteId && !hasSetInitialCaller.current) {
        // Ensure accountId is set in parent state ONCE
        if (accountId && accountId !== "select" && !hasSetAccountId.current) {
          props.onChange({"target": "accountId", "value": accountId});
          hasSetAccountId.current = true;
        }

        // Set the quote ID FIRST - this is critical!
        setCurrentQuoteId(props.info[0].quoteId);

        // Handle caller if it exists
        if (props.info[0].caller) {
          const quoteCaller = props.info[0].caller.trim();

          // Add caller to options list if it's not already there (e.g., old quote with deleted caller)
          if (!callerOptions.includes(quoteCaller) && quoteCaller) {
            const updatedOptions = [...callerOptions, quoteCaller];
            setCallerOptions(updatedOptions);
            setFilteredOptions(updatedOptions);
          }

          setCallerName(quoteCaller);
          setInputValue(quoteCaller);
        } else {
          // Caller is null - check localStorage or leave empty
          const savedCaller = localStorage.getItem(`caller_${accountId}`);
          if (savedCaller) {
            setCallerName(savedCaller);
            setInputValue(savedCaller);
          } else {
            setCallerName(null);
            setInputValue('');
          }
        }

        // Mark that we've initialized from a loaded quote
        hasSetInitialCaller.current = true;
        isLoadingQuote.current = true;
        setIsInitialized(true);
        return; // Exit early, don't check localStorage again
      }

      // Only run the rest if not yet initialized
      if (isInitialized) return;

      // IMPORTANT: If we're currently loading a quote from URL (isLoadingQuote is true),
      // DON'T initialize from localStorage - wait for the quote data to arrive!
      // This prevents creating a new quote when loading an existing one
      if (props.isLoadingQuote) {
        return;
      }

      // Ensure accountId is set in parent state for non-quote initializations
      if (accountId && accountId !== "select" && !hasSetAccountId.current) {
        props.onChange({"target": "accountId", "value": accountId});
        hasSetAccountId.current = true;
      }

      // Priority 2: Loading from localStorage (but only if not skipping due to account change)
      if (!skipLocalStorage) {
        const savedCaller = localStorage.getItem(`caller_${accountId}`);
        if (savedCaller) {
          setCallerName(savedCaller);
          setInputValue(savedCaller);
        } else {
          setCallerName(null);
          setInputValue('');
        }
      } else {
        // Account was changed, skip localStorage and force new selection
        setCallerName(null);
        setInputValue('');
        setSkipLocalStorage(false); // Reset for next time
      }

      setIsInitialized(true);
    };

    initializeCaller();
  }, [accountId, props.info[0]?.quoteId, props.loadedQuoteId, props.isLoadingQuote, skipLocalStorage, isInitialized]);

  // Load caller options when account changes
  useEffect(() => {
    if (!accountId) return;

    httpRequest.getUserEmails(accountId).then((body) => {
      const options = body.data.map((person) => person.username);
      setCallerOptions(options);
      setFilteredOptions(options);
    });
  }, [accountId]);

// Filter options based on input
useEffect(() => {
  if (!inputValue) {
    setFilteredOptions(callerOptions);
  } else {
    const filtered = callerOptions.filter(option => 
      option.toLowerCase().includes(inputValue.toLowerCase())
    );
    setFilteredOptions(filtered);
  }
}, [inputValue, callerOptions]);

  // Handle caller changes and quote ID management
  useEffect(() => {
    if (!isInitialized || !accountId) return;

    const updateQuote = async () => {
      
      const isNameChange = previousCaller.current !== null && previousCaller.current !== callerName;
      
      // No caller selected
      if (!callerName) {
        // Only set accountId if not already set
        if (!hasSetAccountId.current) {
          props.onChange({"target": "accountId", "value": accountId});
          hasSetAccountId.current = true;
        }
        props.onChange({"target": "caller", "value": undefined});
        props.onChange({"target": "quoteId", "value": undefined});
        props.onAlert({"target": "caller", "value": true});
        props.onAlert({"target": "Initial", "value": false});
        setCurrentQuoteId(null);
        previousCaller.current = null;
        return;
      }

      // Valid caller selected
      
      // Only set accountId on first selection, not on name changes
      if (!hasSetAccountId.current) {
        props.onChange({"target": "accountId", "value": accountId});
        hasSetAccountId.current = true;
      }
      
      // Handle quote ID FIRST before updating caller
      try {
        if (isLoadingQuote.current && hasSetInitialCaller.current) {
          // First time loading from URL - don't create new quote, but UPDATE it with the caller
          isLoadingQuote.current = false;

          // Update the quote in the database with the caller
          // This is critical for quotes that have caller=null in the DB
          await httpRequest.postQuoteInfo(
            accountId,
            callerName,
            props.companyID,
            currentQuoteId  // Pass the existing quote ID to update it
          );

          // Ensure parent has the correct quoteId
          props.onChange({"target": "quoteId", "value": currentQuoteId});

          // Now update caller state
          props.onChange({"target": "caller", "value": callerName});

          // Only clear alerts if this is NOT a name change (i.e., first selection)
          if (!isNameChange) {
            props.onAlert({"target": "caller", "value": false});
            props.onAlert({"target": "accountId", "value": false});
            props.onAlert({"target": "Initial", "value": false});
          }
        } else {
          // Create or update quote FIRST
          const response = await httpRequest.postQuoteInfo(
            accountId,
            callerName,
            props.companyID,
            currentQuoteId // Pass existing quoteId to update it
          );

          const newQuoteId = response.data[0].quoteID;
          
          setCurrentQuoteId(newQuoteId);
          
          // Update parent state with quote ID first
          props.onChange({"target": "quoteId", "value": newQuoteId});
          
          // Small delay to ensure quoteId is set in parent state
          await new Promise(resolve => setTimeout(resolve, 50));
          
          // THEN update caller
          props.onChange({"target": "caller", "value": callerName});
          
          // Only clear alerts if this is NOT a name change (i.e., first selection)
          if (!isNameChange) {
            props.onAlert({"target": "caller", "value": false});
            props.onAlert({"target": "accountId", "value": false});
            props.onAlert({"target": "Initial", "value": false});
          }
        }
        
        // Save to localStorage
        localStorage.setItem(`caller_${accountId}`, callerName);
        
        // Update previous caller reference
        previousCaller.current = callerName;
        
      } catch (error) {
        console.error("❌ Error managing quote:", error);
      }
    };

    updateQuote();
  }, [callerName, accountId, isInitialized]);

  // Reset caller-related state when the account switches (driven by nav bar)
  const previousAccountRef = useRef(props.value);
  useEffect(() => {
    if (!props.value) return;
    if (previousAccountRef.current === props.value) return;

    // Skip reset if the account change came from loading a quote — that path
    // manages its own caller/quote state.
    const isQuoteLoadSwitch = props.loadedQuoteId && props.info[0]?.accountId === props.value;
    if (isQuoteLoadSwitch) {
      previousAccountRef.current = props.value;
      return;
    }

    console.log("🔄 Account changed via nav to:", props.value);
    previousAccountRef.current = props.value;

    setCallerName(null);
    setInputValue('');
    setCallerOptions([]);
    setCurrentQuoteId(null);
    setIsInitialized(false);
    hasSetInitialCaller.current = false;
    isLoadingQuote.current = false;
    hasSetAccountId.current = false;
    previousCaller.current = null;
    hasHandledAccountSwitch.current = null;
    setSkipLocalStorage(true);

    props.onChange({"target": "accountId", "value": props.value});
    props.onChange({"target": "caller", "value": undefined});
    props.onChange({"target": "quoteId", "value": undefined});
    props.onAlert({"target": "caller", "value": true});
  }, [props.value]);


const handleInputFocus = () => {
  console.log("🔵 Focus event triggered");
  console.log("🔵 callerOptions length:", callerOptions.length);
  console.log("🔵 isDropdownOpen before:", isDropdownOpen);
  
  // Select all text
  if (inputRef.current) {
    inputRef.current.select();
  }
  
  // Show full unfiltered list and keep it open
  setFilteredOptions(callerOptions);
  setIsDropdownOpen(true);
  if (wrapperRef.current) {
    setAnchorEl(wrapperRef.current);
  }
  
  console.log("🔵 Set dropdown to true");
};

const handleInputChange = (event) => {
  const value = event.target.value;
  setInputValue(value);
  
  // Keep dropdown open - don't close it
  if (!isDropdownOpen && wrapperRef.current) {
    setIsDropdownOpen(true);
    setAnchorEl(wrapperRef.current);
  }
  
  // If user clears the input completely, clear the caller name but keep dropdown open
  if (!value) {
    setCallerName(null);
  }
};
const handleKeyDown = (event) => {
  if (event.key === 'Enter') {
    event.preventDefault();
    // Blur will trigger the selection/creation logic
    if (inputRef.current) {
      inputRef.current.blur();
    }
  }
};

const handleInputBlur = async () => {
  // Delay to allow clicking on menu items
  setTimeout(async () => {
    if (!inputValue || !accountId) {
      if (!inputValue) {
        setCallerName(null);
      }
      return;
    }
    
    // Check if input matches an existing option exactly
    const exactMatch = callerOptions.find(option => 
      option.toLowerCase() === inputValue.toLowerCase()
    );
    
    if (exactMatch) {
      // Use the existing option (preserves exact casing)
      setCallerName(exactMatch);
      setInputValue(exactMatch);
      handleCloseMenu();
    } else {
      // Create new caller
      console.log("➕ Creating new caller:", inputValue);
      
      try {
        await httpRequest.postUserEmails(accountId, {
          username: inputValue,
          emailAddress: ""
        });
        
        // Refresh options
        const response = await httpRequest.getUserEmails(accountId);
        const options = response.data.map((person) => person.username);
        setCallerOptions(options);
        
        // Set as selected
        setCallerName(inputValue);
        
        // Close dropdown after creating
        handleCloseMenu();
      } catch (error) {
        console.error("❌ Error creating new caller:", error);
      }
    }
  }, 200);
};

  // Handle dropdown menu
  const handleOpenMenu = (event) => {
    if (wrapperRef.current) {
      setAnchorEl(wrapperRef.current);
    }
    setIsDropdownOpen(true);
    // When clicking dropdown button, show ALL options (not filtered)
    setFilteredOptions(callerOptions);
    // Keep focus on input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

const handleCloseMenu = () => {
  console.log("🔴 Close menu called");
  console.trace(); // This will show us what called it
  setIsDropdownOpen(false);
  setAnchorEl(null);
};

  const handleSelectFromMenu = (option) => {
    setCallerName(option);
    setInputValue(option);
    handleCloseMenu();
    // Return focus to input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle delete caller
  const handleDeleteCaller = async () => {
    if (!callerName || !accountId) return;

    if (!window.confirm(`Delete caller "${callerName}"?`)) {
      return;
    }

    try {
      await httpRequest.removeCaller(accountId, callerName);
      
      // Update options list
      const updatedOptions = callerOptions.filter(name => name !== callerName);
      setCallerOptions(updatedOptions);
      
      // Clear selection and notify parent
      setCallerName(null);
      setInputValue('');
      localStorage.removeItem(`caller_${accountId}`);
      
      // Reset previous caller tracking
      previousCaller.current = null;
      
      // Clear the quoteId since we no longer have a valid caller
      setCurrentQuoteId(null);
      
      // Notify parent that caller and quote are now invalid
      props.onChange({"target": "caller", "value": undefined});
      props.onChange({"target": "quoteId", "value": undefined});
      
      // Store the current expanded step before closing
      props.onChange({"target": "previousExpandedStep", "value": props.expanded});
      
      props.onAlert({"target": "caller", "value": true});
      
      // Reset all completed steps starting from step 1
      if (props.setCompletedStep) {
        props.setCompletedStep(1);
      }
      
      console.log("🗑️ Deleted caller:", callerName);
    } catch (error) {
      console.error("❌ Error deleting caller:", error);
    }
  };

  const isDisabled = !accountId || accountId === "select";
  const hasError = !callerName && accountId && accountId !== "select";

  return (
    <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12}>
            <Box sx={sxNameInputContainer}>
                <Grid container ref={wrapperRef} alignItems="stretch" style={{ flexGrow: 1 }}>
                    <Grid
                        item
                        xs={4}
                        style={{
                            ...styles.labelWrapper,
                            border: hasError ? `2px solid #f44336` : styles.labelWrapper.border,
                            ...(hasError && { borderRight: 'none' })
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            style={{
                                ...styles.label,
                                color: hasError ? '#f44336' : styles.label.color
                            }}
                        >
                            Name
                        </Typography>
                    </Grid>
                    <Grid item xs={8}>
                        <Box sx={hasError ? { ...sxInputPartBase, ...sxInputPartError } : sxInputPartBase}>
                          <Box
                            component="input"
                            ref={inputRef}
                            type="text"
                            sx={sxInput}
                            value={inputValue}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onKeyDown={handleKeyDown}
                            disabled={isDisabled}
                            placeholder={isDisabled ? "Please select an account first" : ""}
                          />
                            {!isDisabled && (
                            <ArrowDropDownIcon
                                sx={sxDropdownIcon}
                                onClick={handleOpenMenu}
                                onMouseDown={(e) => e.preventDefault()}
                            />
                            )}
                        </Box>
                    </Grid>
                </Grid>
                {callerName && (
                    <IconButton
                    onClick={handleDeleteCaller}
                    size="small"
                    title="Delete this caller"
                    style={{ padding: '8px', marginLeft: '8px' }}
                    >
                    <DeleteIcon style={{ fontSize: '20px' }} />
                    </IconButton>
                )}
            </Box>

          <Popper
            open={isDropdownOpen && !isDisabled}
            anchorEl={anchorEl}
            placement="bottom-start"
            style={{ zIndex: 1300, width: anchorEl ? anchorEl.offsetWidth : 'auto' }}
            modifiers={{
              flip: {
                enabled: true,
              },
              preventOverflow: {
                enabled: true,
                boundariesElement: 'scrollParent',
              },
            }}
          >
            <ClickAwayListener onClickAway={handleCloseMenu} mouseEvent="onMouseDown" touchEvent="onTouchStart">
              <Paper sx={sxMenuPaper}>
                <MenuList style={{ maxHeight: '280px', overflow: 'auto' }}>
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => (
                      <MenuItem
                        key={index}
                        onClick={() => handleSelectFromMenu(option)}
                        onMouseDown={(e) => e.preventDefault()}
                        sx={sxMenuItem}
                        selected={option === callerName}
                      >
                        {option}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled sx={sxMenuItem}>
                      {inputValue ? 'No matches - press Tab to create new' : 'No callers saved yet'}
                    </MenuItem>
                  )}
                </MenuList>
              </Paper>
            </ClickAwayListener>
          </Popper>

          {hasError && (
            <Box sx={sxHelperText}>
              Name is required
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default DropdownBox;